/**
 * useAgentCreation — One-shot agent call for activity creation extraction
 *
 * Sends the user's free text to the RASC agent backend which:
 *   1. Extracts structured fields (category, date, participants)
 *   2. Geocodes addresses to lat/lng coordinates
 *   3. Optionally creates the activity if ALL fields are present
 *
 * Returns the agent's uiResult (collectedData, progress, etc.)
 * so the CreateActivityAssistant can populate its chip flow.
 *
 * Falls back gracefully — caller should catch errors and use
 * client-side NLP (extractFieldsFromText) as fallback.
 *
 * @ticket RAS-57
 */

import { useCallback, useRef } from 'react';
import { auth } from '../../../firebase';
import { useAuth } from '../../../core/providers/AuthProvider';
import { usePreferences } from '../../../shared/hooks/usePreferences';
import buildAgentContext from '../../../shared/agent/agentContextBuilder';
import { Conversation } from '../../../domain/agent/entities/Conversation';
import SendMessageToAgent from '../../../use-cases/agent/SendMessageToAgent';
import { repositories } from '../../../infrastructure/config/repositories';
import { logger } from '../../../shared/utils/logger';

// ---------------------------------------------------------------------------
// GPS helpers (same pattern as useAssistantChat)
// ---------------------------------------------------------------------------

const GPS_TTL_MS = 10 * 60 * 1000; // 10 min cache

const getGpsLocation = () =>
  new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        }),
      () => resolve(null),
      { timeout: 10000, enableHighAccuracy: false }
    );
  });

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * @returns {{ extractActivity: (text: string) => Promise<AgentExtractionResult> }}
 *
 * @typedef {Object} AgentExtractionResult
 * @property {Object|null} uiResult       — Structured response (collectedData, progress, ctas…)
 * @property {Object|null} metadata       — toolsUsed, blocked, latency…
 * @property {string}      message        — Assistant text response
 * @property {Array}       actionCards    — Visual action cards
 */
export const useAgentCreation = () => {
  const { user } = useAuth();
  const { preferences } = usePreferences();
  const lastGpsRef = useRef({ location: null, timestamp: 0 });
  const sendMessageUseCaseRef = useRef(
    new SendMessageToAgent(repositories.agent)
  );
  const conversationRef = useRef(new Conversation());

  /**
   * Send user text to the RASC agent and return the structured extraction.
   *
   * @param {string} text — Free-text description (e.g. "Match de foot demain au stade")
   * @returns {Promise<AgentExtractionResult>}
   * @throws {Error} AUTH_REQUIRED if user is not authenticated
   */
  const extractActivity = useCallback(
    async (text) => {
      // Reset conversation to avoid leaking prior prompts into new extractions
      conversationRef.current = new Conversation();

      // 1. Auth token
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error('AUTH_REQUIRED');

      // 2. GPS location (priority: manual pref > cached GPS > fresh GPS > profile)
      const currentPosition = preferences?.currentPosition;
      let gpsLocation = null;

      if (currentPosition?.latitude && currentPosition?.longitude) {
        gpsLocation = {
          latitude: currentPosition.latitude,
          longitude: currentPosition.longitude,
        };
      } else {
        const now = Date.now();
        const isGpsFresh =
          now - lastGpsRef.current.timestamp < GPS_TTL_MS;
        if (isGpsFresh && lastGpsRef.current.location) {
          gpsLocation = lastGpsRef.current.location;
        } else {
          gpsLocation = await getGpsLocation();
          if (gpsLocation) {
            lastGpsRef.current = { location: gpsLocation, timestamp: now };
          }
        }
      }

      // 3. Build agent context
      const context = buildAgentContext({
        gpsLocation,
        profileLocation: user?.location,
        preferences,
        interests: user?.interests,
        platform: 'web',
        outputFormat: 'ui_result_v1',
      });

      // 4. Send to agent
      logger.debug('useAgentCreation — sending to agent:', {
        textLength: text.length,
        hasGps: !!gpsLocation,
      });

      const result = await sendMessageUseCaseRef.current.execute({
        message: text,
        conversation: conversationRef.current,
        authToken: token,
        context,
      });

      // 5. Extract and return response
      const assistantMessage = result.assistantMessage;

      logger.debug('useAgentCreation — agent response:', {
        hasUiResult: !!assistantMessage?.uiResult,
        blocked: assistantMessage?.metadata?.blocked,
        toolsUsed: assistantMessage?.metadata?.toolsUsed,
      });

      return {
        uiResult: assistantMessage?.uiResult || null,
        metadata: assistantMessage?.metadata || null,
        message: assistantMessage?.content || '',
        actionCards: assistantMessage?.cards || [],
      };
    },
    [user, preferences]
  );

  return { extractActivity };
};
